import type { ReactNode } from 'react';
import { Box, Flex, Image, Text, Badge, Button, HStack, Avatar } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { ZARA_NAV, type StepStatus } from '../routes';
import { rsGradient } from '../theme/reachSocialTheme';
import { useAuth } from '../auth/AuthContext';
import { BrandPicker } from './BrandPicker';

// Shared sidebar chrome. The legacy nav and the /v2 IA both use these
// pieces so a flag-on shell does not introduce a second visual language.
// Flag off still renders the same nodes: BrandMark defaults to /home.

type ShellProps = {
  logoTo?: string;
  testId?: string;
  children: ReactNode;
};

export function SidebarShell({ logoTo = '/home', testId, children }: ShellProps) {
  const auth = useAuth();
  return (
    <Flex
      as="aside"
      direction="column"
      w="240px"
      flexShrink={0}
      h="100vh"
      bg="brand.surface"
      borderRightWidth="1px"
      borderRightColor="brand.border"
      position="sticky"
      top={0}
      px={5}
      py={6}
      data-testid={testId}
    >
      <BrandMark to={logoTo} />

      {auth.status === 'authenticated' && (
        <Box mt={6}>
          <BrandPicker />
        </Box>
      )}

      {children}

      <SidebarAccount />
    </Flex>
  );
}

export function BrandMark({ to = '/home' }: { to?: string }) {
  return (
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      <Flex align="center" gap={3} _hover={{ opacity: 0.85 }} cursor="pointer">
        <Image src="/reach-social-logo.png" alt="Reach Social" w="36px" h="36px" />
        <Box>
          <Text fontWeight="800" color="brand.ink" lineHeight="1.1">Reach Social</Text>
          <Text fontSize="xs" color="brand.muted">AI creative pipeline</Text>
        </Box>
      </Flex>
    </NavLink>
  );
}

export function ZaraNavItem({ isActive }: { isActive: boolean }) {
  return (
    <Flex
      align="center"
      gap={3}
      px={3}
      py={3}
      borderRadius="xl"
      bg={isActive ? 'rgba(110, 86, 207, 0.10)' : 'transparent'}
      transition="background 120ms"
      _hover={{ bg: isActive ? 'rgba(110, 86, 207, 0.10)' : 'gray.50' }}
    >
      <Box
        w="28px"
        h="28px"
        borderRadius="lg"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="#6E56CF"
        bg="rgba(110, 86, 207, 0.12)"
      >
        <Sparkles size={14} />
      </Box>
      <Box flex={1} minW={0}>
        <Text fontSize="sm" fontWeight="700" color={isActive ? '#5B45B8' : 'brand.ink'} lineHeight="1.2">
          {ZARA_NAV.label}
        </Text>
        <Text fontSize="xs" color="brand.muted" noOfLines={1}>
          {ZARA_NAV.description}
        </Text>
      </Box>
    </Flex>
  );
}

export function NavItem({
  index, icon, label, description, status, isActive
}: {
  // Numbered pipeline items pass `index` (1..N); the unnumbered Home
  // entry passes an `icon` React node instead. Exactly one of the two
  // should be set; if both are, index wins (matches STEPS.map order
  // semantics). No status badge for the icon variant — status is a
  // pipeline concept.
  index?:      number;
  icon?:       React.ReactNode;
  label:       string;
  description: string;
  status?:     StepStatus;
  isActive:    boolean;
}) {
  const showAsActive = isActive;
  return (
    <Flex
      align="center"
      gap={3}
      px={3}
      py={3}
      borderRadius="xl"
      bg={showAsActive ? 'rsViolet.50' : 'transparent'}
      transition="background 120ms"
      _hover={{ bg: showAsActive ? 'rsViolet.50' : 'gray.50' }}
    >
      <Box
        w="28px"
        h="28px"
        borderRadius="lg"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        fontWeight="800"
        color={showAsActive ? 'white' : 'brand.muted'}
        bgImage={showAsActive ? rsGradient : undefined}
        bg={showAsActive ? undefined : 'gray.100'}
      >
        {index ?? icon}
      </Box>
      <Box flex={1} minW={0}>
        <Text
          fontSize="sm"
          fontWeight="700"
          color={showAsActive ? 'rsViolet.700' : 'brand.ink'}
          lineHeight="1.2"
        >
          {label}
        </Text>
        <Text fontSize="xs" color="brand.muted" noOfLines={1}>
          {description}
        </Text>
      </Box>
      {status && <StatusBadge status={status} />}
    </Flex>
  );
}

export function SecondaryNavItem({ label, isActive, badgeCount = 0 }: { label: string; isActive: boolean; badgeCount?: number }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      px={3}
      py={2}
      borderRadius="md"
      bg={isActive ? 'rsViolet.50' : 'transparent'}
      transition="background 120ms"
      _hover={{ bg: isActive ? 'rsViolet.50' : 'gray.50' }}
    >
      <Text
        fontSize="sm"
        fontWeight={isActive ? '700' : '600'}
        color={isActive ? 'rsViolet.700' : 'brand.muted'}
        lineHeight="1.2"
      >
        {label}
      </Text>
      {badgeCount > 0 && (
        <Badge
          variant="solid"
          colorScheme="red"
          fontSize="9px"
          borderRadius="full"
          px={1.5}
          minW="18px"
          textAlign="center"
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </Badge>
      )}
    </Flex>
  );
}

function StatusBadge({ status }: { status: StepStatus }) {
  if (status === 'complete') return <Badge variant="complete" fontSize="9px">Done</Badge>;
  if (status === 'active')   return <Badge variant="processing" fontSize="9px">Now</Badge>;
  if (status === 'warning')  return <Badge variant="warning" fontSize="9px">!</Badge>;
  return null;
}

function SidebarAccount() {
  const auth = useAuth();
  return (
    <Box pt={6} mt={4} borderTopWidth="1px" borderTopColor="brand.border">
      {auth.status === 'authenticated' ? (
        <HStack spacing={3}>
          <Avatar name={auth.user.name} src={auth.user.photo ?? undefined} size="sm" />
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>
              {auth.user.name}
            </Text>
            <Text fontSize="xs" color="brand.muted" noOfLines={1}>{auth.user.email}</Text>
          </Box>
          <Button onClick={auth.signOut} variant="ghost" size="xs">Sign out</Button>
        </HStack>
      ) : auth.status === 'loading' ? (
        <Text fontSize="xs" color="brand.muted">Loading session…</Text>
      ) : (
        <Button onClick={auth.signIn} variant="brand" w="full" size="sm">Sign in</Button>
      )}
    </Box>
  );
}
